import { create } from "zustand";
import type { ConfigResponse } from "../../api";
import type { IPublicClientApplication } from "@azure/msal-browser";
import type { SecuritySetting } from "../types";

// Permissions Enumerator
export enum Permission {
    Reader = 1,
    Analyst = 2,
    Admin = 3
}

const VIEW_AS_PERMISSION_STORAGE_KEY = "admin.viewAsPermission";

const readStoredViewAsPermission = (): Permission | null => {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const storedPermission = window.localStorage.getItem(VIEW_AS_PERMISSION_STORAGE_KEY);
        if (storedPermission === String(Permission.Reader)) return Permission.Reader;
        if (storedPermission === String(Permission.Analyst)) return Permission.Analyst;
    } catch {
        return null;
    }

    return null;
};

const storeViewAsPermission = (permission: Permission | null): void => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        if (permission === null) {
            window.localStorage.removeItem(VIEW_AS_PERMISSION_STORAGE_KEY);
        } else {
            window.localStorage.setItem(VIEW_AS_PERMISSION_STORAGE_KEY, String(permission));
        }
    } catch {
        return;
    }
};

// Helper function to get permission level from role name
const getPermissionFromRole = (roleName: string): Permission | null => {
    const normalizedRole = roleName.toLowerCase().trim();
    switch (normalizedRole) {
        case "reader":
            return Permission.Reader;
        case "analyst":
            return Permission.Analyst;
        case "admin":
            return Permission.Admin;
        default:
            return null;
    }
};

// Helper function to check if user has any matching role from backend roles
// userRoles contains definitions like ["Task.Write", "Analyst"]
// backendRoles contains objects with name and definition
const checkRoleMatch = (userRoles: string[], backendRoles: { name: string; definition: string }[]): { hasMatch: boolean; highestPermission: Permission | null; matchedRoles: string[] } => {
    let highestPermission: Permission | null = null;
    const matchedRoles: string[] = [];

    for (const userRoleDefinition of userRoles) {
        // Find backend role that has this definition
        const matchingBeRole = backendRoles.find(beRole => beRole.definition === userRoleDefinition);

        if (matchingBeRole) {
            const permission = getPermissionFromRole(matchingBeRole.name);
            if (permission !== null) {
                matchedRoles.push(matchingBeRole.name);
                if (highestPermission === null || permission > highestPermission) {
                    highestPermission = permission;
                }
            }
        }
    }

    return {
        hasMatch: highestPermission !== null,
        highestPermission,
        matchedRoles
    };
};

const getEffectivePermission = (state: SecuritySetting): number | null => {
    if (state.viewAsPermission !== null && state.isActualAdmin()) {
        return state.viewAsPermission;
    }
    return state.authRequired ? state.userPermission : Permission.Admin;
};

export const useSecuritySetting = create<SecuritySetting>((set, get) => ({
    initialized: false,
    tenantId: "",
    clientId: "",
    scopes: [],
    authRequired: true,
    msalInstance: null,
    roles: [],
    beRoles: [],
    loading: false,
    userPermission: null,
    viewAsPermission: readStoredViewAsPermission(),

    isAdmin: () => {
        return getEffectivePermission(get()) === Permission.Admin;
    },
    isAnalyst: () => {
        const permission = getEffectivePermission(get());
        return permission !== null && permission >= Permission.Analyst;
    },
    isReader: () => {
        const permission = getEffectivePermission(get());
        return permission !== null && permission >= Permission.Reader;
    },
    isActualAdmin: () => {
        const { authRequired, userPermission } = get();
        return !authRequired || userPermission === Permission.Admin;
    },
    updatePermissions: () => {
        const { roles, beRoles, authRequired } = get();

        if (!authRequired) {
            console.log("Store: Auth not required, skipping permission check");
            return;
        }

        // Check if in local mode
        if (String(beRoles) === "local") {
            set({ userPermission: Permission.Admin });
            console.log("Store: Local mode - user is admin");
            return;
        }

        if (beRoles.length === 0) {
            console.warn("Store: No backend roles defined");
        }

        const { hasMatch, highestPermission, matchedRoles } = checkRoleMatch(roles, beRoles);

        if (highestPermission !== Permission.Admin) {
            storeViewAsPermission(null);
        }

        set((state) => ({
            userPermission: highestPermission,
            viewAsPermission: highestPermission === Permission.Admin ? state.viewAsPermission : null,
        }));

        if (!hasMatch && roles.length > 0) {
            console.warn("Store: User roles do not match any backend roles", { userRoles: roles, beRoles });
        } else if (highestPermission !== null) {
            console.log(`Store: User has ${Permission[highestPermission]} permission (matched roles: ${matchedRoles.join(", ")})`);
        }
    },

    init: async (response: ConfigResponse) => {
        console.log("Store: Loading AAD config");
        const authRequired = response.security_settings.auth_required;
        set({
            clientId: response.security_settings.client_id,
            tenantId: response.security_settings.tenant_id,
            scopes: response.security_settings.scopes,
            authRequired,
            beRoles: response.security_settings.roles,
            viewAsPermission: authRequired ? null : readStoredViewAsPermission(),
            initialized: true,
        });

        console.log("Store: Setting roles and admin status");
        get().updatePermissions();
        console.log("Store: AAD config loaded");
    },
    changeLoading: () => {
        set((state) => ({ loading: !state.loading }));
    },
    setMsalInstance: (instance: IPublicClientApplication) => {
        set({ msalInstance: instance });
        console.log("Store: MSAL instance set");
    },
    validateMsalSession: async () => {
        const { authRequired, scopes, msalInstance } = get();

        if (!authRequired) {
            return true;
        }

        if (!msalInstance) {
            console.warn("Store: MSAL instance is not available");
            return false;
        }

        const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
        if (!account) {
            console.warn("Store: No MSAL account found, session is no longer valid");
            return false;
        }

        try {
            await msalInstance.acquireTokenSilent({ scopes, account });
            return true;
        } catch (error) {
            console.warn("Store: MSAL session validation failed", error);
            return false;
        }
    },
    getAccessToken: async () => {
        const { authRequired, scopes, msalInstance } = get();
        if (authRequired && msalInstance) {
            const accounts = msalInstance.getAllAccounts();
            try {
                const response = await msalInstance.acquireTokenSilent({ scopes: scopes, account: accounts[0] });
                console.log("Returning access token");
                return response.accessToken;
            } catch (error) {
                console.error("Error fetching data:", error);
                // reload page or handle login using msal, or throw error which can be processed later
            }
        }
        return "";
    },
    setRoles(roles: string[]) {
        console.log(roles);
        set({ roles: roles });
        get().updatePermissions();
    },
    setViewAsPermission(permission: number | null) {
        if (!get().isActualAdmin()) {
            return;
        }
        const nextPermission = permission === Permission.Admin ? null : permission as Permission | null;
        if (nextPermission !== null && nextPermission !== Permission.Reader && nextPermission !== Permission.Analyst) {
            return;
        }
        storeViewAsPermission(nextPermission);
        set({ viewAsPermission: nextPermission });
    },
}));
